import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ListChecks, Users, Package, ShoppingCart, Archive, MessageSquare, LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, key: "nav.home", icon: LayoutDashboard },
  { to: "/admin/commandes-en-cours", key: "nav.active_orders", icon: ListChecks },
  { to: "/admin/clients", key: "nav.clients", icon: Users },
  { to: "/admin/packages", key: "nav.packages", icon: Package },
  { to: "/admin/commandes", key: "nav.orders", icon: ShoppingCart },
  { to: "/admin/archives", key: "nav.archives", icon: Archive },
  { to: "/admin/contact", key: "nav.contact", icon: MessageSquare },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/contact/unread").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    window.addEventListener("contact-updated", load);
    const tmr = setInterval(load, 15000);
    return () => { clearInterval(tmr); window.removeEventListener("contact-updated", load); };
  }, []);

  const doLogout = async () => {
    await logout();
    toast.success(t("common.logout"));
    navigate("/login");
  };

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, end, key, icon: Icon }) => (
        <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${to.split("/").pop() || "home"}`}
          className={({ isActive }) =>
            `group flex items-center justify-between rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-black text-white" : "text-neutral-500 hover:bg-neutral-100 hover:text-black"
            }`
          }>
          <span className="flex items-center gap-3">
            <Icon className="h-4 w-4" strokeWidth={1.75} /> {t(key)}
          </span>
          {key === "nav.contact" && unread > 0 && (
            <span data-testid="admin-contact-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-white">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-neutral-200 bg-white p-6 lg:flex">
        <div className="mb-10"><Logo /></div>
        <NavItems />
        <div className="mt-auto border-t border-neutral-200 pt-4">
          <p className="truncate px-3 text-xs text-neutral-400">{user?.email}</p>
          <div className="mt-2 flex items-center gap-2">
            <Button variant="ghost" data-testid="admin-logout-button" onClick={doLogout}
              className="flex-1 justify-start gap-3 rounded-sm px-3 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-black">
              <LogOut className="h-4 w-4" /> {t("common.logout")}
            </Button>
            <LanguageSwitcher />
          </div>
        </div>
      </aside>

      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-3 lg:hidden">
        <Logo size="sm" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher idSuffix="-mobile" />
          <button data-testid="admin-menu-toggle" onClick={() => setOpen(!open)} className="rounded-sm p-2 hover:bg-neutral-100">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 top-[57px] z-30 bg-white p-6 lg:hidden">
          <NavItems />
          <Button variant="ghost" onClick={doLogout} className="mt-6 w-full justify-start gap-3 rounded-sm px-3 text-neutral-600">
            <LogOut className="h-4 w-4" /> {t("common.logout")}
          </Button>
        </div>
      )}

      <main className="flex-1 lg:ml-64">
        <div className="mx-auto max-w-6xl px-6 py-10 pt-20 lg:px-12 lg:pt-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
